import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { DashboardLayout, Panel, Placeholder, StatCard, StatusBadge } from "@/components/DashboardLayout";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function ReferralClientDashboard() {
  const { user } = useAuth();
  const [kyc, setKyc] = useState(null);

  useEffect(() => {
    api.get("/api/kyc/me").then((r) => setKyc(r.data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout
      title={`Welcome, ${user?.full_name?.split(" ")[0]}`}
      subtitle="Referral Client dashboard — complete verification and prepare to fund care."
      badge={<StatusBadge status={user?.kyc_status} testId="rc-kyc-badge" />}
    >
      <div className="grid gap-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="KYC status" value={(user?.kyc_status || "PENDING").replaceAll("_", " ")} testId="rc-stat-kyc" />
          <StatCard label="Escrow balance" value="₦0" hint="Funding opens in Stage 2" testId="rc-stat-balance" />
          <StatCard label="Account type" value="Referral Client" hint="Upgrade path to Master Client available" testId="rc-stat-type" />
        </div>

        <Panel title="Profile" testId="rc-profile-panel">
          <dl className="grid gap-4 sm:grid-cols-2">
            {[["Full name", user?.full_name], ["Email", user?.email], ["Phone", user?.phone], ["Phone verified", user?.phone_verified ? "Yes" : "No"]].map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k}</dt>
                <dd className="mt-1 text-sm text-slate-800">{v}</dd>
              </div>
            ))}
          </dl>
        </Panel>

        {user?.kyc_status !== "VERIFIED" && (
          <Panel title="Verification" description={kyc?.record ? `Current status: ${kyc.status.replaceAll("_", " ")}` : "You have not submitted KYC yet."} testId="rc-kyc-panel">
            {(!kyc?.record || ["PENDING", "REJECTED", "CORRECTION_REQUESTED"].includes(kyc?.status)) && (
              <Button asChild className="rounded-full bg-emerald-900 hover:bg-emerald-800">
                <Link to="/onboarding/referral-client" data-testid="rc-start-kyc-btn">
                  {kyc?.record ? "Resubmit KYC" : "Start KYC"}
                </Link>
              </Button>
            )}
            {kyc?.record?.reviewer_notes && <p className="mt-3 text-sm text-orange-700">Reviewer note: {kyc.record.reviewer_notes}</p>}
          </Panel>
        )}

        <Placeholder title="Fund a booking" body="Referral Clients get their own escrow wallet once the Master Client upgrade path is enabled." testId="rc-funding-placeholder" />
      </div>
    </DashboardLayout>
  );
}

export function ReferrerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [kyc, setKyc] = useState(null);

  const load = () => api.get("/api/kyc/referrer/me").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/api/kyc/me").then((r) => setKyc(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    try {
      const { data: res } = await api.post("/api/kyc/referrer/referral-code");
      toast.success(`Referral code: ${res.referral_code}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const code = data?.profile?.referral_code;

  return (
    <DashboardLayout
      title={`Welcome, ${user?.full_name?.split(" ")[0]}`}
      subtitle="Referrer dashboard — verification, referral code and your referred families."
      badge={<StatusBadge status={user?.kyc_status} testId="rf-kyc-badge" />}
    >
      <div className="grid gap-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="KYC status" value={(user?.kyc_status || "PENDING").replaceAll("_", " ")} testId="rf-stat-kyc" />
          <StatCard label="Referrals" value={data?.referrals?.length ?? 0} testId="rf-stat-referrals" />
          <StatCard label="Classification" value={data?.profile?.classification?.replaceAll("_", " ") || "—"} testId="rf-stat-class" />
        </div>

        {!data?.profile && (
          <Panel title="Complete your onboarding" description="Submit your baseline KYC and relationship declaration to generate a referral code." testId="rf-kyc-panel">
            <Button asChild className="rounded-full bg-emerald-900 hover:bg-emerald-800">
              <Link to="/onboarding/referrer" data-testid="rf-start-kyc-btn">Start onboarding</Link>
            </Button>
          </Panel>
        )}

        {data?.profile && (
          <Panel title="Your referral code" description="Share this code with families you introduce to CARENEST." testId="rf-code-panel">
            {code ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50 px-5 py-3 font-mono text-lg font-bold text-emerald-800" data-testid="rf-referral-code">
                  {code}
                </span>
                <Button variant="outline" className="rounded-full" onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }} data-testid="rf-copy-code-btn">
                  <Copy className="mr-2 h-4 w-4" /> Copy
                </Button>
              </div>
            ) : (
              <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={generate} data-testid="rf-generate-code-btn">
                Generate referral code
              </Button>
            )}
            {kyc?.record?.reviewer_notes && <p className="mt-3 text-sm text-orange-700">Reviewer note: {kyc.record.reviewer_notes}</p>}
          </Panel>
        )}

        <Panel title="Referred clients" testId="rf-referrals-panel">
          {data?.referrals?.length ? (
            <ul className="divide-y divide-slate-100">
              {data.referrals.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-3 text-sm">
                  <span className="font-medium text-slate-800">{r.full_name}</span>
                  <StatusBadge status={r.kyc_status} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No referred clients yet.</p>
          )}
        </Panel>

        <Placeholder title="Referral bonuses" body="Commission tracking and payouts arrive in Stage 3." testId="rf-bonuses-placeholder" />
      </div>
    </DashboardLayout>
  );
}


